'use client';

/**
 * Spec 80 §7.2 + spec 90 — the "Ask Zero" help widget for the docs site.
 *
 * Dual-mode (spec 90 §2): the widget always POSTs to `/api/docs/ask`;
 * the SERVER selects the harness from the session. Anonymous visitors
 * get the docs-grounded lean (spec 80 §12.3) — no auth, no account
 * tools, read-only. Signed-in customers get one turn of their account's
 * implicit Zero agent; the response then carries `mode: 'account'` plus
 * a link to continue the same thread in the app, or
 * `mode: 'account_picker'` when the server needs an explicit account
 * choice, or `degraded: 'out_of_credits'` + a top-up URL when the
 * wallet is empty (spec 90 §4.4).
 *
 * Graceful degradation: any non-OK response (503 while the platform key
 * is unwired, 429 rate-limited, 5xx/network) collapses to a "browse the
 * docs" note with a link to `/docs`. Citations render as chips
 * deep-linking `/docs/<slug>#<anchor>` (the API returns formed urls).
 *
 * Client component — it only fetches; it never imports server/domain
 * code (@repo/control-plane-domain would bundle pg into the browser
 * chunk). shadcn/marketing-ui tokens, FontAwesome icons, Tailwind v4.
 */
import { faArrowRight, faCircleUser, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/ui/components/ui/button';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { AsyncButton } from '@/components/async-button';

interface Citation {
  title: string;
  url: string;
}

/** Union of the route's three payload shapes (see route.ts). */
interface AskResult {
  mode: 'anonymous' | 'account';
  answer: string;
  citations: Citation[];
  /** account mode only */
  account_slug?: string;
  thread_url?: string;
  proposed_action_count?: number;
  /** degraded anonymous (spec 90 §4.4) */
  degraded?: 'out_of_credits';
  top_up_url?: string;
}

interface PickerAccount {
  slug: string;
  name: string;
}

const FALLBACK_MESSAGE =
  'Ask Zero is unavailable right now — browse the docs below or use the search on the docs index.';

/** Suggested starter prompts keyed on the page's product area. */
const SUGGESTIONS_BY_AREA: Record<string, string[]> = {
  connectors: ['How do I connect Notion?', 'What is a connector mount prefix?'],
  agents: ['How do I create an agent?', 'How do I change an agent’s model?'],
  zero: ['What can Zero do for me?', 'How do I approve an action Zero proposes?'],
  concepts: ['What is the difference between an account and a project?', 'What is a thread?'],
  security: ['Who can act in my account?', 'Where is my data stored?'],
  billing: ['How do credits work?', 'How do I set a spend cap?'],
  models: ['Which models can my agents use?', 'How do I assign a cheaper model?'],
  skills: ['What is a skill?', 'How do I add a skill to an agent?'],
  usage: ['How is my usage measured?', 'Where do I see my spend?'],
  'getting-started': ['How do I get started with connect0?', 'How do I connect my first data source?'],
};
const DEFAULT_SUGGESTIONS = ['How do I get started with connect0?', 'How do I connect a data source?'];

function suggestionsFor(productArea?: string): string[] {
  return (productArea && SUGGESTIONS_BY_AREA[productArea]) || DEFAULT_SUGGESTIONS;
}

export function AskZeroWidget({ productArea }: { productArea?: string }) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<AskResult | null>(null);
  const [picker, setPicker] = useState<PickerAccount[] | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const suggestions = suggestionsFor(productArea);

  // Every trigger (Ask button, Enter, suggestion chip, picker choice)
  // routes through one guarded in-flight path: an AbortController cancels
  // the previous request and a monotonic sequence number ensures a stale
  // response can never overwrite a newer one.
  const abortRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);

  async function ask(question: string, accountSlug?: string) {
    const q = question.trim();
    if (!q) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++seqRef.current;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/docs/ask', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question: q, product_area: productArea, account_slug: accountSlug }),
        signal: controller.signal,
      });
      if (seq !== seqRef.current) return; // a newer request superseded this one
      if (!res.ok) {
        setResult(null);
        setPicker(null);
        setError(FALLBACK_MESSAGE);
        return;
      }
      const data = (await res.json()) as {
        mode?: string;
        accounts?: PickerAccount[];
        answer?: unknown;
        citations?: unknown;
        account_slug?: unknown;
        thread_url?: unknown;
        proposed_action_count?: unknown;
        degraded?: unknown;
        top_up_url?: unknown;
      };
      if (seq !== seqRef.current) return;
      if (data.mode === 'account_picker' && Array.isArray(data.accounts)) {
        // Spec 90 §4.2 — the server needs an explicit account; hold the
        // question and re-ask with the choice.
        setResult(null);
        setPicker(data.accounts);
        setPendingQuestion(q);
        return;
      }
      setPicker(null);
      setPendingQuestion(null);
      setResult({
        mode: data.mode === 'account' ? 'account' : 'anonymous',
        answer: typeof data.answer === 'string' ? data.answer : '',
        citations: Array.isArray(data.citations) ? data.citations : [],
        account_slug: typeof data.account_slug === 'string' ? data.account_slug : undefined,
        thread_url: typeof data.thread_url === 'string' ? data.thread_url : undefined,
        proposed_action_count: typeof data.proposed_action_count === 'number' ? data.proposed_action_count : 0,
        degraded: data.degraded === 'out_of_credits' ? 'out_of_credits' : undefined,
        top_up_url: typeof data.top_up_url === 'string' ? data.top_up_url : undefined,
      });
    } catch {
      if (controller.signal.aborted || seq !== seqRef.current) return;
      setResult(null);
      setPicker(null);
      setError(FALLBACK_MESSAGE);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }

  return (
    <section
      aria-label="Ask Zero"
      className="mt-12 flex flex-col gap-4 rounded-sm border border-border-primary bg-surface-card-secondary p-5 md:p-6"
    >
      <div className="flex items-center gap-2">
        <FontAwesomeIcon icon={faWandMagicSparkles} aria-hidden className="h-4 w-4 text-accent-violet-500" />
        <h2 className="font-label font-medium text-label-md text-text-heading-1">Ask Zero</h2>
      </div>
      <p className="font-label text-label-sm text-text-caption-1">
        Ask a question about connect0 and get an answer grounded in the docs, with links to the sources. Signed in? Zero
        answers with your account in mind.
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && query.trim().length > 0 && !loading) {
              e.preventDefault();
              void ask(query);
            }
          }}
          placeholder="Ask about connect0…"
          aria-label="Ask about connect0"
          className="grow rounded-xxs border border-input bg-surface-field px-4 py-3 font-label text-label-md text-text-filled outline-none transition-colors placeholder:text-text-caption-1 focus:border-border-action"
        />
        <AsyncButton
          type="button"
          onClick={() => ask(query)}
          disabled={query.trim().length === 0 || loading}
          pendingLabel="Thinking…"
          className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-surface-action-2 px-6 text-text-on-action-1 hover:bg-surface-action-hover-2"
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} aria-hidden className="h-4 w-4" />
          Ask Zero
        </AsyncButton>
      </div>

      {/* Suggested prompts (hidden once there's an answer to keep the card calm). */}
      {!result && !error && !picker ? (
        <div className="flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <Button
              key={s}
              type="button"
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => {
                setQuery(s);
                void ask(s);
              }}
              className="border-border-primary font-normal text-text-caption-1"
            >
              {s}
            </Button>
          ))}
        </div>
      ) : null}

      {error ? <p className="font-label text-label-sm text-text-caption-1">{error}</p> : null}

      {/* Spec 90 §4.2 — account picker: re-ask the held question with the choice. */}
      {picker ? (
        <div className="flex flex-col gap-3 rounded-sm border border-border-primary bg-surface-card-primary p-4">
          <p className="font-label text-label-sm text-text-caption-1">Which account should Zero answer for?</p>
          <div className="flex flex-wrap gap-2">
            {picker.map((a) => (
              <Button
                key={a.slug}
                type="button"
                variant="outline"
                size="sm"
                disabled={loading}
                onClick={() => void ask(pendingQuestion ?? query, a.slug)}
                className="border-border-primary"
              >
                <FontAwesomeIcon icon={faCircleUser} aria-hidden className="h-3.5 w-3.5 text-icon-secondary" />
                {a.name}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {result ? (
        <div className="flex flex-col gap-4 rounded-sm border border-border-primary bg-surface-card-primary p-4 md:p-5">
          {/* Spec 90 §4.4 — degraded banner: docs answers keep working,
              account features wait for a top-up. */}
          {result.degraded === 'out_of_credits' && result.top_up_url ? (
            <p className="rounded-xxs border border-border-primary bg-surface-card-secondary px-3 py-2 font-label text-label-sm text-text-caption-1">
              Your account is out of credits, so Zero answered from the docs only.{' '}
              <Link href={result.top_up_url} className="text-text-on-action-2 underline underline-offset-4">
                Top up credits
              </Link>{' '}
              to bring back account-aware answers.
            </p>
          ) : null}
          {result.answer ? (
            <p className="whitespace-pre-wrap font-body text-body-sm text-text-body">{result.answer}</p>
          ) : null}
          {result.mode === 'account' && result.thread_url ? (
            <p className="font-label text-label-sm text-text-caption-1">
              Answered with your account’s Zero.
              {result.proposed_action_count ? (
                <> {result.proposed_action_count} proposed action(s) await your review.</>
              ) : null}{' '}
              <Link href={result.thread_url} className="text-text-on-action-2 underline underline-offset-4">
                Continue this conversation in the app
              </Link>
              .
            </p>
          ) : null}
          {result.mode !== 'account' && result.citations.length > 0 ? (
            <div className="flex flex-col gap-2">
              <span className="overline-text text-text-overline">Sources</span>
              <ul className="flex flex-col gap-1.5">
                {result.citations.map((c) => (
                  <li key={c.url}>
                    <Link
                      href={c.url}
                      className="inline-flex items-center gap-1.5 font-label font-medium text-label-md text-text-on-action-2 underline-offset-4 hover:underline"
                    >
                      <FontAwesomeIcon icon={faArrowRight} aria-hidden className="h-3.5 w-3.5 text-icon-secondary" />
                      {c.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {result.mode !== 'account' && result.citations.length === 0 ? (
            <p className="font-label text-label-sm text-text-caption-1">
              No specific doc matched — browse the full{' '}
              <Link href="/docs" className="text-text-on-action-2 underline underline-offset-4">
                documentation
              </Link>
              .
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
